import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import ConditionalWrapper from '../../../ui/ConditionalWrapper';
import {
  Absolute,
  BodyText,
  HeaderText,
  Stack,
  Touchable,
} from '../../../ui';
import { ArrowsChevronLeftIcon } from '../../../ui/Icons';
import { useGoBack } from '../../../../hooks';
import { Z_FG } from '../../../ui/utilities/zIndex';
import { HOME_ROUTE } from '../../../../route-names';
import { clickable } from '../../../../styles';

export type MobileHeaderProps = {
  header?: string;
  subHeader?: string;
  hideBackButton?: boolean;
  onBack?: null | (() => void);
  rightContent?: React.ReactNode;
};

const MobileHeader = ({
  header,
  subHeader,
  hideBackButton = false,
  onBack,
  rightContent,
}: MobileHeaderProps) => {
  const goBack = useGoBack();

  const handleGoBack = React.useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
    } else {
      goBack(HOME_ROUTE);
    }
  }, [
    onBack,
    goBack,
  ]);

  return (
    <Inline
      flexGrow={1}
      color={BrandColor.WhiteWash}
      borderColor={BrandColor.LightGrey}
      borderBottomWidth={1}
      borderStyle="solid"
      alignItems="center"
      maxHeight={60}
      minHeight={45}
      position="relative"
      zIndex={Z_FG}
      testID="MobileHeader"
    >
      {hideBackButton ? null : (
        <Absolute
          left={0}
          zIndex={Z_FG}
        >
          <Touchable
            onClick={handleGoBack}
            testID="proBackButton"
          >
            <Box paddingX={12} style={clickable}>
              <ArrowsChevronLeftIcon />
            </Box>
          </Touchable>
        </Absolute>
      )}
      <Stack
        flexGrow={1}
        flexShrink={1}
        justifyContent="center"
        alignItems="center"
        paddingY={20}
      >
        {header ? (
          <HeaderText
            level={4}
            ariaLevel={1}
            color={BrandColor.Metal}
            textAlign="center"
            testID="mobileHeaderTitle"
          >
            {header}
          </HeaderText>
        ) : null}
        {subHeader ? (
          <BodyText
            appearance="smaller"
            color={BrandColor.Ash}
            weight="medium"
            textAlign="center"
            testID="mobileHeaderSubtitle"
          >
            {subHeader}
          </BodyText>
        ) : null}
      </Stack>
      {rightContent ? (
        <Absolute right={0} zIndex={Z_FG} testID="mobileHeaderRight">
          {rightContent}
        </Absolute>
      ) : null}
    </Inline>
  );
};

const WrapperHOC = (onClick, testID) => wrappedChildren => (
  <Touchable onClick={onClick} testID={testID}>{wrappedChildren}</Touchable>
);

export const RightButton = ({
  onClick,
  children,
  testID,
}: {
  onClick?: null | ((e: any) => void);
  children: React.ReactNode;
  testID?: string;
}) => (
  <ConditionalWrapper
    condition={typeof onClick === 'function'}
    wrapper={WrapperHOC(onClick, testID)}
  >
    <Box paddingX={12} color={BrandColor.WhiteWash} testID={testID}>
      <BodyText
        color={BrandColor.Ash}
        textTransform="uppercase"
        appearance="smaller"
        weight="bold"
      >
        {children}
      </BodyText>
    </Box>
  </ConditionalWrapper>
);

export default MobileHeader;
